import React from 'react'

import {
  BRAND_EDGE_QUESTIONS,
  BRAND_EDGE_TAGLINE_QUESTION,
  BVA_SECTIONS,
  HUMAN_TENSION_QUESTIONS,
  HUMAN_TENSION_TAGLINE_QUESTION,
  MEANINGFUL_BRAND_ROLE_QUESTION,
  PRODUCT_EDGE_QUESTIONS,
  PRODUCT_EDGE_TAGLINE_QUESTION,
} from 'constants/bvaQuestions'
import { Section } from 'model/section'
import { getAnswer } from 'utils/bvaSections'

import './HumanTension.scss'

interface Props {
  sections: Section[]
}

interface QuestionProps {
  title: string | React.ReactNode
  text: string
  side: 'top' | 'left' | 'right'
}

interface Item {
  title: string
  text: string
}

const Question: React.FC<QuestionProps> = ({ title, text, side }) => (
  <div className={`human-tension__question human-tension__question--${side}`}>
    <div className="human-tension__question-title">{title}</div>
    <div className="human-tension__question-rule" />
    <p>{text}</p>
  </div>
)

export const HumanTension: React.FC<Props> = ({ sections }) => {
  const humanTension = (question: string) => getAnswer(sections, BVA_SECTIONS.humanTension, question)
  const productEdge = (question: string) => getAnswer(sections, BVA_SECTIONS.productEdge, question)
  const brandEdge = (question: string) => getAnswer(sections, BVA_SECTIONS.brandEdge, question)
  const meaningfulBrandRole = getAnswer(sections, BVA_SECTIONS.meaningfulBrandRole, MEANINGFUL_BRAND_ROLE_QUESTION)

  const [sensorialCues, reasonToBelieve, productSuperiorityInsights, functionalBenefit] = PRODUCT_EDGE_QUESTIONS
  const leftItems: Item[] = [
    { title: sensorialCues, text: productEdge(sensorialCues) },
    { title: reasonToBelieve, text: productEdge(reasonToBelieve) },
    { title: productSuperiorityInsights, text: productEdge(productSuperiorityInsights) },
    { title: functionalBenefit, text: productEdge(functionalBenefit) },
  ]

  const [personality, memoryStructure, brandPhilosophy, emotionalBenefit] = BRAND_EDGE_QUESTIONS
  const rightItems: Item[] = [
    { title: personality, text: brandEdge(personality) },
    { title: memoryStructure, text: brandEdge(memoryStructure) },
    { title: brandPhilosophy, text: brandEdge(brandPhilosophy) },
    { title: emotionalBenefit, text: brandEdge(emotionalBenefit) },
  ]

  const [whoAreYouFor, whatAreTheirBeliefValues, whatNeedAreTheyTryingToFulfill] = HUMAN_TENSION_QUESTIONS

  return (
    <section className="human-tension" aria-label="Human Tension brand role">
      <div className="human-tension__background human-tension__background--top" />
      <div className="human-tension__background human-tension__background--left" />
      <div className="human-tension__background human-tension__background--right" />

      <div className="human-tension__content">
        <div className="human-tension__top-row">
          <header className="human-tension__intro">
            <h1>HUMAN TENSION</h1>
            <p>{humanTension(HUMAN_TENSION_TAGLINE_QUESTION)}</p>
          </header>

          <div className="human-tension__top human-tension__top--one">
            <Question title={whoAreYouFor} text={humanTension(whoAreYouFor)} side="top" />
          </div>

          <div className="human-tension__top human-tension__top--two">
            <Question
              title={
                <>
                  What are their
                  <br />
                  belief/values?
                </>
              }
              text={humanTension(whatAreTheirBeliefValues)}
              side="top"
            />
          </div>

          <div className="human-tension__top human-tension__top--three">
            <Question
              title={
                <>
                  What need are they
                  <br />
                  trying to fulfill?
                </>
              }
              text={humanTension(whatNeedAreTheyTryingToFulfill)}
              side="top"
            />
          </div>
        </div>

        <div className="human-tension__left-stack">
          {leftItems.map(item => (
            <Question key={item.title} {...item} side="left" />
          ))}
        </div>

        <div className="human-tension__right-stack">
          {rightItems.map(item => (
            <Question key={item.title} {...item} side="right" />
          ))}
        </div>

        <div className="human-tension__center">
          <div className="human-tension__ring">
            <div className="human-tension__center-inner">
              <div className="human-tension__center-title">
                MEANINGFUL
                <br />
                BRAND ROLE
              </div>
              <p>{meaningfulBrandRole}</p>
            </div>
          </div>
        </div>

        <div className="human-tension__bottom human-tension__bottom--product">
          <strong>PRODUCT EDGE</strong>
          <span>{productEdge(PRODUCT_EDGE_TAGLINE_QUESTION)}</span>
        </div>

        <div className="human-tension__bottom human-tension__bottom--brand">
          <strong>BRAND EDGE</strong>
          <span>{brandEdge(BRAND_EDGE_TAGLINE_QUESTION)}</span>
        </div>
      </div>
    </section>
  )
}
